import { useCallback, useEffect, useState } from 'react';
import { ActionCatalogDto, catalogService, TriggerEntity, TriggerEvent } from '../services/catalogService';

export interface CatalogState {
  entities: TriggerEntity[];
  events: TriggerEvent[];
  actions: ActionCatalogDto[];
  isLoading: boolean;
  isFetched: boolean;
}

export const useCatalog = () => {
  const [state, setState] = useState<CatalogState>({
    entities: [],
    events: [],
    actions: [],
    isLoading: false,
    isFetched: false,
  });

  const fetchCatalog = useCallback(async () => {
    if (state.isFetched || state.isLoading) return;

    setState(prev => ({ ...prev, isLoading: true }));
    try {
      const data = await catalogService.getEntities();
      const entities = Array.isArray(data) ? data : [];

      setState(prev => ({
        ...prev,
        entities,
        isLoading: false,
        isFetched: true,
      }));

      // Fetch events and actions for each entity to have a full mapping
      const [eventResults, actionResults] = await Promise.all([
        Promise.all(entities.map(entity => catalogService.getEvents(entity.entityType))),
        Promise.all(entities.map(entity => catalogService.getActions(entity.entityType))),
      ]);

      const allEvents: TriggerEvent[] = [];
      eventResults.flat().forEach(newEvent => {
        if (!allEvents.find(e => e.eventId === newEvent.eventId)) {
          allEvents.push(newEvent);
        }
      });

      const allActions: ActionCatalogDto[] = [];
      actionResults.flat().forEach(newAction => {
        if (!allActions.find(a => a.actionId === newAction.actionId)) {
          allActions.push(newAction);
        }
      });

      setState(prev => ({
        ...prev,
        events: allEvents,
        actions: allActions,
      }));
    } catch (error) {
      console.error('Failed to fetch catalog', error);
      setState(prev => ({ ...prev, isLoading: false, isFetched: true }));
    }
  }, [state.isFetched, state.isLoading]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  const getEntityName = useCallback(
    (type: string): string => {
      const entity = state.entities.find(e => e.entityType === type);
      return entity?.entityName || type;
    },
    [state.entities]
  );

  const getEventName = useCallback(
    (id: string): string => {
      const event = state.events.find(e => e.eventId === id);
      return event?.eventName || id;
    },
    [state.events]
  );

  return {
    ...state,
    fetchCatalog,
    getEntityName,
    getEventName,
  };
};
